import { useState, useCallback } from 'react';
import { message } from 'antd';
import { pw9201Api, F90B, F90B1 } from '../../../services/pw9201Api';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './usePW9201Mode';

export function usePW9201Record() {
  const [record, setRecord] = useState<F90B | null>(null);
  const [formData, setFormData] = useState<Partial<F90B>>({});
  const [details, setDetails] = useState<Partial<F90B1>[]>([]);
  const [loading, setLoading] = useState(false);
  const { setFieldErrors, clearFieldErrors, getFieldError, hasErrors } = useFieldErrors();

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string): Promise<F90B | null> => {
    setLoading(true);
    try {
      const data = await pw9201Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      setDetails(data.details || []);
      return data;
    } catch {
      message.error('载入失败');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback(<K extends keyof F90B>(key: K, value: F90B[K]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback(<K extends keyof F90B>(key: K): F90B[K] | string => {
    return (formData as Partial<F90B>)[key] ?? '';
  }, [formData]);

  /** Update a detail row field */
  const updateDetailField = useCallback((rowIdx: number, field: keyof F90B1, value: F90B1[keyof F90B1]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[rowIdx]) {
        next[rowIdx] = { ...next[rowIdx], [field]: value };
      }
      return next;
    });
  }, []);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: F90B) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as F90B,
        details: details as F90B1[],
      };
      if (mode === 'add') {
        const result = await pw9201Api.create(req);
        message.success(`新增成功: ${result.f90b_01}`);
        onSuccess(result);
      } else {
        const result = await pw9201Api.update(formData.f90b_01!, req);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        setDetails(result.details || []);
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        message.error('请修正标示的栏位');
      } else {
        message.error(err.response?.data?.error || '储存失败');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details, setFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await pw9201Api.delete(record.f90b_01);
      message.success('删除成功');
      return true;
    } catch {
      message.error('删除失败');
      return false;
    }
  }, [record]);

  /** Add a new detail row */
  const addDetailRow = useCallback(() => {
    setDetails(prev => [
      ...prev,
      {
        f90b1_01: formData.f90b_01 || '',
        f90b1_02: prev.length + 1,
        f90b1_11: '',
        f90b1_11a: '',
        f90b1_12: 0,
        f90b1_13: 0,
        f90b1_14: 0,
        f90b1_15: null,
        f90b1_21: '',
        f90b1_22: 0,
        f90b1_23: '',
        f90b1_24: '',
        f90b1_25: '',
        f90b1_26: '',
        f90b1_27: '',
        f90b1_281: false,
        f90b1_282: false,
        f90b1_32: '',
        f90b1_32a: '',
      },
    ]);
  }, [formData.f90b_01]);

  /** Delete a detail row by index */
  const deleteDetailRow = useCallback((rowIdx: number) => {
    setDetails(prev => prev.filter((_, i) => i !== rowIdx));
  }, []);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record, clearFieldErrors]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    setFormData({
      f90b_11: '',
      f90b_12: '',
      f90b_13: null,
      f90b_20: 0,
    });
    setDetails([]);
  }, [clearFieldErrors]);

  /** Apply result after save */
  const applyResult = useCallback((result: F90B) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  return {
    record, formData, details, loading,
    setDetails,
    loadRecord, setField, getField,
    updateDetailField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult,
    getFieldError, clearFieldErrors, hasErrors,
  };
}
